/**
 * Asks the browser about the microphone, and lets the dispatcher grant it deliberately.
 *
 * Two things kept apart on purpose. Checking is passive and runs on its own: it reads the
 * permission and counts the devices, and never triggers a dialog. Asking is active, runs
 * only from a click, and is the only path that can produce one — because a dialog nobody
 * expected is a dialog that gets dismissed.
 *
 * See `microphone.ts` for what the answers mean.
 */

import { useCallback, useEffect, useState } from 'react';

import {
  classify,
  fromError,
  type MicrophoneReadiness,
  type PermissionAnswer,
} from './microphone';

/** How many audio inputs the browser admits to, which is zero when it will not say. */
async function countInputs(): Promise<number> {
  if (!navigator.mediaDevices?.enumerateDevices) return 0;

  try {
    const devices = await navigator.mediaDevices.enumerateDevices();

    return devices.filter((device) => device.kind === 'audioinput').length;
  }
  catch {
    return 0;
  }
}

/**
 * What the Permissions API says, or null where it will not answer.
 *
 * Firefox has never implemented the `microphone` name and throws on it, and a browser that
 * refuses the question is not the same as one that says no — hence null, which `classify`
 * turns into `unknown` rather than into a refusal the dispatcher never made.
 */
async function readPermission(): Promise<{ answer: PermissionAnswer; status: PermissionStatus | null }> {
  if (!navigator.permissions?.query) return { answer: null, status: null };

  try {
    const status = await navigator.permissions.query({
      name: 'microphone' as PermissionName,
    });

    return { answer: status.state as PermissionAnswer, status };
  }
  catch {
    return { answer: null, status: null };
  }
}

export interface Microphone {
  readiness: MicrophoneReadiness;

  /** True while a request is open, so the button can say so and not be pressed twice. */
  asking: boolean;

  /** Opens the browser's own prompt. Only ever call this from a person's click. */
  ask: () => Promise<void>;

  /** Re-reads without prompting, for after somebody has been to the browser's settings. */
  recheck: () => Promise<void>;
}

export function useMicrophone(): Microphone {
  const [readiness, setReadiness] = useState<MicrophoneReadiness>('ready');
  const [asking, setAsking] = useState(false);

  const recheck = useCallback(async () => {
    const [{ answer }, inputs] = await Promise.all([readPermission(), countInputs()]);

    setReadiness(classify(answer, inputs));
  }, []);

  const ask = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setReadiness('unknown');
      return;
    }

    setAsking(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Let go of it immediately. The grant is what was wanted; holding the track open
      // would leave the recording indicator lit for the whole shift, which looks to a
      // dispatcher exactly like being listened to.
      for (const track of stream.getTracks()) track.stop();

      setReadiness('ready');
    }
    catch (error) {
      setReadiness(fromError(error));
    }
    finally {
      setAsking(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let status: PermissionStatus | null = null;

    const onChange = () => {
      if (!cancelled) void recheck();
    };

    void (async () => {
      const [{ answer, status: handle }, inputs] = await Promise.all([
        readPermission(),
        countInputs(),
      ]);

      if (cancelled) return;

      setReadiness(classify(answer, inputs));

      // Granting it in the browser's settings, in another tab, or by answering a prompt
      // raised somewhere else all arrive here — so the warning clears itself rather than
      // waiting for a reload that a dispatcher mid-shift is not going to perform.
      status = handle;
      status?.addEventListener('change', onChange);
    })();

    // A microphone unplugged mid-shift is the same problem arriving later.
    navigator.mediaDevices?.addEventListener?.('devicechange', onChange);

    return () => {
      cancelled = true;
      status?.removeEventListener('change', onChange);
      navigator.mediaDevices?.removeEventListener?.('devicechange', onChange);
    };
  }, [recheck]);

  return { readiness, asking, ask, recheck };
}
